import React from 'react';
import { Typography } from '@guildeducationinc/recess';
import { GetMemberProgramQuery } from '@src/graphqlTypes/catalogServiceTypes';
import { formatMoney } from '@src/utilities/numberFormattingUtility';
import { getRemainingCosts } from '@src/constants/cost';
import { usePricebook } from '@src/components/Providers/PricebookProvider';
import { Skeleton } from '@src/components/UserFacetSkeletonLoader';
import { CostPopover } from '../../CostPopover';
import styles from './CostHighlight.module.scss';

interface CostHighlightProps {
	loading: boolean;
	program: GetMemberProgramQuery['memberProgram'];
}

export const CostHighlight: React.FC<CostHighlightProps> = ({ loading, program }) => {
	const { hideTuitionWhenNoOutOfPocket, programCost, programCostLoading, programCostUnavailable } =
		usePricebook();
	const remainingCosts = getRemainingCosts(program.fundingLevel, programCost);
	const tuition = formatMoney(programCost);
	const funding = formatMoney(program.fundingLevel);
	const cost = formatMoney(remainingCosts);
	const fundingText =
		program.fundingLevel === -1 ? '100% tuition paid' : `Up to ${funding} per year`;
	const hideTuition = hideTuitionWhenNoOutOfPocket && remainingCosts === 0;

	return (
		<div className={styles.highlight} data-testid='highlight-Cost'>
			<Typography className={styles.label1} variant='label1'>
				Cost
			</Typography>
			<Skeleton
				dataLoading={loading || programCostLoading}
				height={48}
				style={{ margin: '8px 0' }}
				width={300}
			>
				{!hideTuition && (
					<div className={styles.highlightItem}>
						<Typography className={styles.label2} variant='label2'>
							Tuition:
						</Typography>
						{programCostUnavailable ? (
							'unavailable'
						) : (
							<Typography variant='body1'>{tuition}</Typography>
						)}
						<CostPopover eventText='tuition' id='tuition' location='highlight' />
					</div>
				)}

				<div className={styles.highlightItem}>
					<Typography className={styles.label2} variant='label2'>
						Funding:
					</Typography>
					<Typography variant='body1'>{fundingText}</Typography>
					<CostPopover eventText='funding' funding={funding} id='funding' location='highlight' />
				</div>

				<div className={styles.highlightItem}>
					<Typography className={styles.label2} variant='label2'>
						Estimated cost:
					</Typography>
					{programCostUnavailable ? 'unavailable' : <Typography variant='body1'>{cost}</Typography>}
					<CostPopover eventText='estimated cost' id='cost' location='highlight' />
				</div>
			</Skeleton>
		</div>
	);
};
